<template>
  <div v-if="paymentMethods">
    <div class="text-h6 text-uppercase text-weight-bold q-mb-md">
      {{ $t('payment.methods') }}
    </div>

    <div v-if="!currentDeliveryMethod">
      {{ $t('payment.noDeliveryChosen') }}
    </div>

    <div
      v-else
      class="row q-col-gutter-lg"
    >
      <div
        v-for="paymentMethod in availablePaymentMethods"
        :key="paymentMethod.id"
        :class="{
          'col-xs-12 col-sm-6 col-md-2': !catering,
          'col-12': catering,
        }"
        @click="selectPaymentMethod(paymentMethod)"
      >
        <div
          class="paymentMethod cursor-pointer text-body1 text-center
            rounded-borders q-py-lg q-px-xl full-height row"
          :class="{
            selected: currentPaymentMethod && currentPaymentMethod.id === paymentMethod.id
          }"
        >
          <div
            v-if="isVerifone(paymentMethod) || isValitor(paymentMethod) || isAur(paymentMethod)"
            class="col-12 row items-end justify-center q-mb-sm"
          >
            <div v-if="isVerifone(paymentMethod)" class="col-12">
              <img
                svg-inline
                width="90px"
                src="quasar-pilot-core/src/assets/verifone-logo.svg"
              >
            </div>

            <div v-if="isValitor(paymentMethod)" class="col-12">
              <img
                svg-inline
                width="110px"
                src="quasar-pilot-core/src/assets/valitor-logo.svg"
              >
            </div>

            <div v-if="isAur(paymentMethod)" class="col-12">
              <q-img
                v-if="isAur(paymentMethod)"
                width="48px"
                src="~quasar-pilot-core/src/assets/aur-logo.svg"
              />
            </div>
          </div>

          <div
            class="col-12 row items-center justify-center no-wrap text-h6 text-uppercase"
            style="line-height: 1.15"
          >
            <q-radio
              v-model="selected"
              class="on-left hidden"
              :val="paymentMethod.id"
              @input="selectPaymentMethod(paymentMethod)"
              @click="selectPaymentMethod(paymentMethod)"
            />

            {{ translateTitle(paymentMethod) }}
          </div>

          <div v-if="paymentMethod.priceImpact">
            {{ paymentMethod.priceImpact }} kr.
          </div>
        </div>
      </div>

      <div
        v-if="showOptionsField"
        :class="{
          'col-xs-12 col-md-6': !catering,
          'col-12': catering,
        }"
      >
        <div
          class="paymentMethod selected row text-body1 text-center
          rounded-borders q-py-lg q-pl-lg full-height full-width items-center"
        >
          <PaymentCardForm
            v-if="selectedIsValitor || selectedIsVerifone"
            class="col-12"
          />

          <PaymentAurForm
            v-if="selectedIsAur"
            class="col-12"
          />

          <div
            v-if="selectedIsValitorPage"
            class="col-12"
          >
            Greiðsla mun fara fram á greiðslusíðu Valitor.
          </div>
          <div
            v-if="selectedIsInvoice"
            class="col-12"
          >
            Upphæð verður sett í reikning á kennitölu {{ externalId }}
          </div>
          <InvoiceAuthForm
            v-if="selectedIsInvoiceQR"
            class="col-12"
          />
        </div>
      </div>
    </div>

    <component
      v-if="hasVerifone"
      :src="verifoneJsUrl"
      :is="'script'"
    />
    <component
      v-if="hasVerifone"
      :src="songbirdUrl"
      :is="'script'"
    />
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import { mapActions, mapMutations, mapState } from 'vuex';
import language from '../../mixins/util/language';
import { constants } from '../../lib/constants';

const verifoneJsUrl = process.env.RELEASE === 'production'
  ? 'https://emea.jsclient.verifone.cloud/verifone.js'
  : 'https://cst.jsclient.vficloud.net/verifone.js';

const songbirdUrl = process.env.RELEASE === 'production'
  ? 'https://songbird.cardinalcommerce.com/edge/v1/songbird.js'
  : 'https://songbirdstag.cardinalcommerce.com/edge/v1/songbird.js';

export default {
  name: 'OrderPaymentMethods',
  components: {
    InvoiceAuthForm: defineAsyncComponent(() => import('../payment/InvoiceAuthForm')),
    PaymentCardForm: defineAsyncComponent(() => import('../payment/CardForm')),
    PaymentAurForm: defineAsyncComponent(() => import('../payment/AurForm')),
  },

  mixins: [language],

  props: {
    catering: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showOptionsField: false,
      selected: null,
      verifoneJsUrl,
      songbirdUrl,
    };
  },

  computed: {
    ...mapState('payment', { paymentMethods: 'all' }),
    ...mapState('order', {
      currentPaymentMethod: 'paymentMethod',
      currentDeliveryMethod: 'deliveryMethod',
    }),
    ...mapState('user', ['externalId', 'role']),
    ...mapState('application', ['isQR']),

    selectedIsVerifone() {
      return (this.currentPaymentMethod?.type === constants.PAYMENT_METHOD_TYPE_VERIFONE);
    },

    selectedIsValitor() {
      return (this.currentPaymentMethod?.type === constants.PAYMENT_METHOD_TYPE_VALITOR);
    },

    selectedIsValitorPage() {
      return (this.currentPaymentMethod?.type === constants.PAYMENT_METHOD_TYPE_VALITORPAGE);
    },

    selectedIsAur() {
      return (this.currentPaymentMethod?.type === constants.PAYMENT_METHOD_TYPE_AUR);
    },

    selectedIsKass() {
      return (this.currentPaymentMethod?.type === constants.PAYMENT_METHOD_TYPE_KASS);
    },

    selectedIsInvoice() {
      return (this.currentPaymentMethod?.type === constants.PAYMENT_METHOD_TYPE_INVOICE && !this.isQR);
    },

    selectedIsInvoiceQR() {
      return (this.currentPaymentMethod?.type === constants.PAYMENT_METHOD_TYPE_INVOICE && this.isQR);
    },

    availablePaymentMethods() {
      if (this.externalId) {
        return this.currentDeliveryMethod.paymentMethods
      }
      return this.currentDeliveryMethod.paymentMethods.filter((p) => p.type !== 100)
    },

    hasVerifone() {
      return (this.currentDeliveryMethod.paymentMethods.find(
        (p) => p.type === constants.PAYMENT_METHOD_TYPE_VERIFONE
      ));
    }
  },

  watch: {
    currentPaymentMethod() {
      if (!this.currentPaymentMethod) {
        this.showOptionsField = false;
      }
    }
  },

  created() {
    // Automatically select the first payment option
    if (!this.currentPaymentMethod && this.currentDeliveryMethod) {
      this.selectPaymentMethod(this.currentDeliveryMethod.paymentMethods[0]);
    }

    if (this.currentPaymentMethod) {
      this.selectPaymentMethod(this.currentPaymentMethod);
    }
  },

  methods: {
    ...mapActions('order', ['updateTotalPrice']),
    ...mapMutations('order', ['updatePaymentMethod']),

    isVerifone(paymentMethod) {
      return (paymentMethod.type === constants.PAYMENT_METHOD_TYPE_VERIFONE);
    },

    isValitor(paymentMethod) {
      return (paymentMethod.type === constants.PAYMENT_METHOD_TYPE_VALITOR
        || paymentMethod.type === constants.PAYMENT_METHOD_TYPE_VALITORPAGE);
    },

    isAur(paymentMethod) {
      return (paymentMethod.type === constants.PAYMENT_METHOD_TYPE_AUR);
    },

    selectPaymentMethod(paymentMethod) {
      this.updatePaymentMethod(paymentMethod);
      this.updateTotalPrice();
      this.selected = paymentMethod.id;
      this.showOptionsField = (this.currentPaymentMethod.type > 0);
    },
  },
};
</script>

<style lang="scss" scoped>
.paymentMethod {
  border: 1px solid $table-border-color;
}

.selected {
  border-color: $primary;
}
</style>
